/**
 * TierCard Component
 * Individual subscription tier card with pricing, features, and CTA
 *
 * Pattern: Mobile-first vertical card using @talktoast/ui components
 * Design: Expandable features via FeatureAccordion, full-width buttons
 */

import { useState } from 'react'
import { Card, Button, Badge } from '@talktoast/ui'
import { SubscriptionTier, BillingInterval, TIER_PRICING } from '@talktoast/shared/types/subscription'
import { FeatureAccordion } from './FeatureAccordion'

interface TierCardProps {
  tier: SubscriptionTier
  interval: BillingInterval
  currentTier?: SubscriptionTier
  highlighted?: boolean
  onSelect: (tier: 'premium', interval: BillingInterval) => void
}

// Tier configuration
const TIER_CONFIG = {
  free: {
    name: 'Free',
    description: 'Perfect for getting started',
    features: [
      '4 meal plans per month',
      '5 AI chat messages per week',
      '3 re-rolls per recipe',
      '5 cuisines',
      'Direct Instacart integration',
      'Recipe favorites & history'
    ],
    detailedFeatures: []
  },
  premium: {
    name: 'Premium',
    description: 'Most popular for home cooks',
    features: [
      'Unlimited meal plans',
      '20 AI chat messages per week',
      '10 re-rolls per recipe',
      '18+ cuisines',
      'Direct Instacart integration',
      'Calorie targeting'
    ],
    detailedFeatures: [
      'Advanced cuisine filtering',
      'Sync with Google Calendar or iCal',
      'Extended recipe history (90 days)',
      '100 favorite recipes'
    ]
  }
}

export function TierCard({ tier, interval, currentTier, highlighted = false, onSelect }: TierCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const config = TIER_CONFIG[tier]
  const isCurrentTier = currentTier === tier
  const isPaidTier = tier !== 'free'

  // Get pricing
  const getPrice = () => {
    if (tier === 'free') return { amount: 0, period: 'Forever free' }

    const pricing = TIER_PRICING[tier]
    const amount = interval === 'monthly' ? pricing.monthly : pricing.yearly
    const monthlyEquivalent = interval === 'yearly' ? (pricing.yearly / 12).toFixed(2) : pricing.monthly.toFixed(2)

    return {
      amount,
      period: interval === 'monthly' ? '/month' : '/year',
      monthlyEquivalent: interval === 'yearly' ? `$${monthlyEquivalent}/mo` : null
    }
  }

  const price = getPrice()

  // Handle CTA click
  const handleSelect = () => {
    if (tier === 'free' || isCurrentTier) return
    onSelect(tier as 'premium', interval)
  }

  // Get CTA button text
  const getCtaText = () => {
    if (tier === 'free') {
      return isCurrentTier ? 'Current Plan' : 'Get Started Free'
    }
    if (isCurrentTier) return 'Current Plan'
    return 'Start 7-Day Free Trial'
  }

  return (
    <Card
      variant={highlighted ? 'elevated' : 'default'}
      padding="md"
      className={`
        mb-3 relative
        ${highlighted ? 'border-2 border-toast-gold pt-6' : 'border-2 border-gray-200'}
        ${isCurrentTier ? 'bg-gray-50' : ''}
      `}
    >
      {/* Highlighted badge */}
      {highlighted && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <Badge variant="success" size="sm" className="bg-green-700 text-white font-bold px-3 py-1 shadow-md whitespace-nowrap">
            RECOMMENDED
          </Badge>
        </div>
      )}

      {/* Header: Name */}
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-gray-900 mb-1">{config.name}</h3>
        <p className="text-sm text-gray-600">{config.description}</p>
      </div>

      {/* Pricing */}
      <div className="text-center mb-4">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-3xl font-bold text-gray-900">
            ${tier === 'free' ? '0' : price.amount.toFixed(2).replace('.00', '')}
          </span>
          <span className="text-gray-600">{price.period}</span>
        </div>
        {price.monthlyEquivalent && (
          <p className="text-sm text-gray-700 font-medium mt-1">{price.monthlyEquivalent} when billed annually</p>
        )}
      </div>

      {/* CTA Button */}
      <Button
        variant={tier === 'free' ? 'secondary' : 'primary'}
        size="lg"
        fullWidth
        onClick={handleSelect}
        disabled={isCurrentTier}
        className="mb-4"
      >
        {getCtaText()}
      </Button>

      {/* Features List */}
      <ul className="space-y-2">
        {config.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <svg className="w-5 h-5 text-avocado-green mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Expandable detailed features (Premium & Pro only) */}
      {config.detailedFeatures.length > 0 && (
        <FeatureAccordion
          title={`See all ${config.name.toLowerCase()} features`}
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
        >
          <ul className="space-y-2">
            {config.detailedFeatures.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <span className="text-gray-500">•</span>
                <span className="text-gray-600">{feature}</span>
              </li>
            ))}
          </ul>
        </FeatureAccordion>
      )}
    </Card>
  )
}
